import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  ApiFailure,
  restoreActiveWorkspace,
  setActiveWorkspace,
  type Session,
  type Workspace,
  type Bucket,
  type Dashboard,
  type Dataset,
  type DatasetSummary,
  type Health,
  type Report,
  type UploadResult,
} from './api';
import { useThemeMode, useVizColors } from './theme';
import { Insights, NarrativePanel, Notice, QualityPanel, Scorecards } from './components/Panels';
import { CategoryChart, TrendChart } from './components/Charts';
import { DataTab, ExploreTab, PivotTab, ReportsTab } from './components/Tabs';
import { Sources } from './components/Sources';
import { Forecasting } from './components/Forecasting';
import { DashboardBuilder } from './components/DashboardBuilder';
import { HowItWorks } from './components/HowItWorks';
import { AiAnalytics } from './components/AiAnalytics';
import { LookerStudio } from './components/LookerStudio';
import { Auth } from './components/Auth';
import { Settings } from './components/Settings';
import { AdminPanel } from './components/admin/AdminPanel';
import { Alerts } from './components/Alerts';
import { SharedReportView } from './components/SharedReport';
import { PortalView } from './components/PortalView';
import { TemplateMarketplace } from './components/TemplateMarketplace';
import { PowerBI } from './components/PowerBI';
import { ClientPortal } from './components/ClientPortal';
import { ClientReports } from './components/ClientReports';
import { LandingPage } from './components/LandingPage';
import { AiChat } from './components/AiChat';
import { MultiSource } from './components/MultiSource';
import { RevenueAnalytics } from './components/RevenueAnalytics';
import { WebsiteSetup } from './components/WebsiteSetup';
import { CustomerJourneys } from './components/CustomerJourneys';
import { AttributionComparison } from './components/AttributionComparison';
import { CampaignSpendTracker } from './components/CampaignSpend';
import { Funnels } from './components/Funnels';
import { Retention } from './components/Retention';
import { Traffic } from './components/Traffic';
import { Realtime } from './components/Realtime';
import { Marketing } from './components/Marketing';
import { Icon } from './components/Icon';
import { CommandPalette, type PaletteItem } from './components/CommandPalette';
import './components/analytics/analytics.css';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/Confirm';

type Page =
  | 'traffic'
  | 'realtime'
  | 'marketing'
  | 'sources'
  | 'overview'
  | 'explore'
  | 'data'
  | 'pivot'
  | 'forecast'
  | 'dashboard'
  | 'reports'
  | 'looker'
  | 'templates'
  | 'powerbi'
  | 'portals'
  | 'client-reports'
  | 'chat'
  | 'compare'
  | 'settings'
  | 'ai'
  | 'revenue'
  | 'websites'
  | 'journeys'
  | 'attribution'
  | 'campaign-spend'
  | 'funnels'
  | 'retention'
  | 'alerts'
  | 'help'
  | 'admin';

interface NavItem {
  id: Page;
  label: string;
  icon: string;
  needsData: boolean;
  /** Extra words the command palette matches on. */
  keywords?: string;
}

/**
 * The navigation, grouped by the job a person is doing rather than by how the
 * code is organised: understand the website, work with a dataset, share the
 * result, configure the workspace.
 */
const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: 'Website analytics',
    items: [
      { id: 'traffic', label: 'Traffic', icon: 'traffic', needsData: false, keywords: 'visitors pageviews sources pages countries devices plausible' },
      { id: 'realtime', label: 'Realtime', icon: 'realtime', needsData: false, keywords: 'live now current visitors' },
      { id: 'revenue', label: 'Revenue', icon: 'revenue', needsData: false, keywords: 'mrr arr ltv cac' },
      { id: 'marketing', label: 'Marketing ROI', icon: 'marketing', needsData: false, keywords: 'mer roas profit mmm mix model triple whale northbeam' },
      { id: 'attribution', label: 'Attribution', icon: 'attribution', needsData: false, keywords: 'models markov shapley first last touch' },
      { id: 'journeys', label: 'Journeys', icon: 'journeys', needsData: false, keywords: 'customer path touchpoints' },
      { id: 'funnels', label: 'Funnels', icon: 'funnels', needsData: false, keywords: 'conversion steps' },
      { id: 'retention', label: 'Retention', icon: 'retention', needsData: false, keywords: 'cohort' },
      { id: 'campaign-spend', label: 'Ad spend', icon: 'spend', needsData: false, keywords: 'campaign budget costs' },
    ],
  },
  {
    group: 'Data studio',
    items: [
      { id: 'ai', label: 'AI analytics', icon: 'ai', needsData: false, keywords: 'auto dashboard upload' },
      { id: 'sources', label: 'Sources', icon: 'sources', needsData: false, keywords: 'upload csv excel bigquery sheets connect' },
      { id: 'overview', label: 'Overview', icon: 'overview', needsData: true },
      { id: 'explore', label: 'Explore', icon: 'explore', needsData: true },
      { id: 'data', label: 'Table', icon: 'table', needsData: true, keywords: 'rows columns' },
      { id: 'pivot', label: 'Pivot', icon: 'pivot', needsData: true },
      { id: 'forecast', label: 'Forecast', icon: 'forecast', needsData: true, keywords: 'prediction prophet' },
      { id: 'chat', label: 'Ask data', icon: 'chat', needsData: true, keywords: 'ai question chat' },
      { id: 'compare', label: 'Compare', icon: 'compare', needsData: false, keywords: 'multi source' },
    ],
  },
  {
    group: 'Reporting',
    items: [
      { id: 'dashboard', label: 'Dashboards', icon: 'dashboard', needsData: true, keywords: 'builder board' },
      { id: 'reports', label: 'Reports', icon: 'reports', needsData: false },
      { id: 'client-reports', label: 'Client reports', icon: 'clientReports', needsData: true, keywords: 'email agency' },
      { id: 'portals', label: 'Client portals', icon: 'portals', needsData: false, keywords: 'white label agency' },
      { id: 'alerts', label: 'Alerts', icon: 'alerts', needsData: false, keywords: 'notify anomaly' },
      { id: 'templates', label: 'Templates', icon: 'templates', needsData: false },
      { id: 'looker', label: 'Looker Studio', icon: 'looker', needsData: true },
      { id: 'powerbi', label: 'Power BI', icon: 'powerbi', needsData: true },
    ],
  },
  {
    group: 'Workspace',
    items: [
      { id: 'websites', label: 'Sites & tracking', icon: 'websites', needsData: false, keywords: 'snippet script install stripe shopify webhook' },
      { id: 'settings', label: 'Settings', icon: 'settings', needsData: false, keywords: 'branding members sharing schedules' },
      { id: 'help', label: 'How it works', icon: 'help', needsData: false, keywords: 'docs guide' },
      { id: 'admin', label: 'Admin', icon: 'admin', needsData: false, keywords: 'people portals' },
    ],
  },
];

const PAGE_LABEL = new Map<Page, string>(NAV.flatMap((group) => group.items.map((item) => [item.id, item.label] as const)));

function readCollapsed(): Set<string> {
  try {
    const raw = localStorage.getItem('dv-nav-collapsed');
    return new Set(raw === null ? [] : (JSON.parse(raw) as string[]));
  } catch {
    return new Set();
  }
}

const NEEDS_DATA = new Set<Page>(['overview', 'explore', 'data', 'pivot', 'forecast', 'dashboard', 'looker', 'powerbi', 'client-reports', 'chat']);

// --- History routing ---------------------------------------------------------

/**
 * Every page is a path, so a view can be bookmarked, refreshed and shared.
 *
 * There is deliberately no router dependency: the map is one segment per page,
 * with the dataset id as an optional second segment on the pages that act on
 * one (`/explore/some-dataset-id`). The server's SPA fallback serves the shell
 * for any of them, and anything unrecognised lands on the sources page.
 */
interface Route {
  page: Page;
  datasetId: string | null;
}

const PAGE_IDS = new Set<string>(NAV.flatMap((group) => group.items.map((item) => item.id)));

function routeFromPath(pathname: string): Route {
  const segments = pathname.split('/').filter(Boolean).map((segment) => decodeURIComponent(segment));
  const first = segments[0];
  if (first !== undefined && PAGE_IDS.has(first)) {
    const page = first as Page;
    return { page, datasetId: NEEDS_DATA.has(page) ? (segments[1] ?? null) : null };
  }
  // The home page: what a website owner checks first. A workspace without a
  // site lands on its onboarding, which also offers the spreadsheet path.
  return { page: 'traffic', datasetId: null };
}

function pathForRoute(route: Route): string {
  return route.datasetId === null ? `/${route.page}` : `/${route.page}/${encodeURIComponent(route.datasetId)}`;
}

/** What each page would show, said plainly, so a dead end still teaches something. */
const EMPTY_COPY: Partial<Record<Page, { title: string; body: string }>> = {
  overview: {
    title: 'Nothing analysed yet',
    body: 'Upload a spreadsheet or connect a platform. The overview shows headline figures, the findings the analysis computed, and a data-quality report.',
  },
  explore: {
    title: 'Nothing to explore yet',
    body: 'Group, filter and switch chart types against any dataset. Add a source first.',
  },
  data: {
    title: 'No table loaded',
    body: 'The data tab shows the parsed rows and the type inferred for every column.',
  },
  pivot: {
    title: 'No data to cross-tabulate',
    body: 'A pivot needs at least one categorical column. Add a source first.',
  },
  forecast: {
    title: 'Nothing to forecast',
    body: 'Forecasting needs a date column and a numeric measure. Add a source with both.',
  },
  dashboard: {
    title: 'No dataset for a dashboard',
    body: 'Dashboard tiles query a dataset directly, so one has to be loaded before you can build a board.',
  },
  looker: {
    title: 'No dataset to hand to Looker Studio',
    body: 'The template is built from your columns and the charts the analysis recommended.',
  },
};

function AppInner() {
  const [mode, setMode] = useThemeMode();
  const colors = useVizColors(mode);

  const [session, setSession] = useState<Session | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [health, setHealth] = useState<Health | null>(null);
  const [datasets, setDatasets] = useState<DatasetSummary[]>([]);
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [chartData, setChartData] = useState<Record<string, Bucket[]>>({});
  const [warnings, setWarnings] = useState<string[]>([]);
  const [route, setRoute] = useState<Route>(() => routeFromPath(window.location.pathname));
  const page = route.page;
  const [dashboardId, setDashboardId] = useState<string | null>(null);
  const [narrating, setNarrating] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [chartsBusy, setChartsBusy] = useState(false);
  const [chartFailure, setChartFailure] = useState<ApiFailure | null>(null);
  // A monotonic token, so a slow chart materialisation for a dataset the user
  // has already left cannot overwrite the one they are looking at.
  const chartToken = useRef(0);
  const [navOpen, setNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(readCollapsed);

  // Ctrl/⌘-K anywhere toggles the command palette.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const toggleGroup = (group: string): void => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      try {
        localStorage.setItem('dv-nav-collapsed', JSON.stringify([...next]));
      } catch {
        /* not remembered */
      }
      return next;
    });
  };

  const navigate = useCallback((next: Page, datasetId: string | null): void => {
    const nextRoute: Route = { page: next, datasetId: NEEDS_DATA.has(next) ? datasetId : null };
    const path = pathForRoute(nextRoute);
    // The deep-link restore and browser back arrive with the URL already set;
    // pushing again would fill the history with duplicates of the same view.
    if (path !== window.location.pathname) window.history.pushState({}, '', path);
    setRoute(nextRoute);
    setNavOpen(false);
    window.scrollTo({ top: 0 });
  }, []);

  // Browser back/forward: the URL is the source of truth, the state follows.
  useEffect(() => {
    const onPop = (): void => setRoute(routeFromPath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const refreshDatasets = useCallback(() => {
    api
      .listDatasets()
      .then((response) => setDatasets(response.datasets))
      .catch(() => setDatasets([]));
  }, []);

  // Health is public, so it loads regardless of who is calling.
  useEffect(() => {
    api.health().then(setHealth).catch(() => setHealth(null));
  }, []);

  // Restore the session before rendering anything: showing the app and then
  // bouncing to a login screen is worse than a brief blank.
  useEffect(() => {
    restoreActiveWorkspace();
    api
      .me()
      .then((restored) => {
        setSession(restored);
        const stored = restoreActiveWorkspace();
        const known = restored.workspaces.some((w) => w.id === stored);
        setActiveWorkspace(known ? stored : (restored.workspaces[0]?.id ?? null));
        refreshDatasets();
      })
      .catch(() => setSession(null))
      .finally(() => setCheckingSession(false));
  }, [refreshDatasets]);

  function load(result: UploadResult): void {
    chartToken.current += 1; // Invalidate any chart load still in flight.
    setChartsBusy(false);
    setDataset(result.dataset);
    setReport(result.report);
    setChartData(Object.fromEntries(result.chartData.map((c) => [c.id, c.buckets])));
    setChartFailure(null);
    setWarnings([
      ...result.warnings,
      ...(result.skippedRows > 0 ? [`${result.skippedRows} empty row(s) were skipped.`] : []),
    ]);
    setDashboardId(null);
    navigate('overview', result.dataset.id);
    refreshDatasets();
  }

  /**
   * Recompute the overview's chart buckets for a stored dataset.
   *
   * The upload response carries them ready-made; a dataset reopened from the
   * library does not, because the series are a pure function of the rows and
   * the suggestion and are recomputed rather than stored. This runs the same
   * aggregation the upload endpoint ran (`datasets.ts` — limit 25, keyAsc for
   * lines), one query per drawable suggestion.
   */
  const materializeCharts = useCallback(async (datasetId: string, forReport: Report): Promise<void> => {
    const token = ++chartToken.current;
    setChartsBusy(true);
    setChartFailure(null);

    const settled = await Promise.allSettled(
      forReport.charts
        .filter(
          (suggestion) =>
            suggestion.chartType !== 'scatter' &&
            suggestion.chartType !== 'histogram' &&
            suggestion.y !== undefined,
        )
        .slice(0, 6)
        .map(async (suggestion) => ({
          id: suggestion.id,
          buckets: (
            await api.query(datasetId, {
              groupBy: suggestion.x,
              measure: suggestion.y,
              aggregation: suggestion.aggregation,
              limit: 25,
              sort: suggestion.chartType === 'line' ? 'keyAsc' : 'valueDesc',
            })
          ).result.buckets,
        })),
    );

    if (token !== chartToken.current) return; // A newer dataset selection won.

    const buckets: Record<string, Bucket[]> = {};
    let failed = 0;
    for (const entry of settled) {
      if (entry.status === 'fulfilled') buckets[entry.value.id] = entry.value.buckets;
      else failed += 1;
    }
    setChartData(buckets);
    setChartsBusy(false);
    if (failed > 0) {
      // Honest absence: the charts that failed stay hidden and the notice says
      // so, rather than a card drawing a chart of nothing.
      setChartFailure(
        new ApiFailure(
          `${failed} chart(s) could not be recomputed.`,
          'The rest of the analysis is unaffected. Re-open the dataset to retry.',
          0,
        ),
      );
    }
  }, []);

  const openDataset = useCallback(
    async (datasetId: string, reportId?: string, targetPage: Page = 'overview'): Promise<void> => {
      setFailure(null);
      try {
        const [datasetResponse, reportResponse] = await Promise.all([
          api.getDataset(datasetId),
          reportId === undefined ? Promise.resolve(null) : api.getReport(reportId),
        ]);

        setDataset(datasetResponse.dataset);
        setWarnings(datasetResponse.warnings);
        setChartData({});
        setDashboardId(null);

        let resolved: Report | null;
        if (reportResponse !== null) {
          resolved = reportResponse.report;
        } else {
          // Fall back to the most recent report for this dataset, so opening a
          // stored dataset lands on something rather than an empty overview.
          const list = await api.listReports();
          resolved = list.reports.find((r) => r.datasetId === datasetId) ?? null;
        }
        setReport(resolved);

        navigate(targetPage, datasetId);
        refreshDatasets();

        if (resolved !== null) void materializeCharts(datasetId, resolved);
        else setChartsBusy(false);
      } catch (error) {
        setFailure(asFailure(error));
      }
    },
    [refreshDatasets, navigate, materializeCharts],
  );

  // A deep link (/explore/:datasetId) opens its dataset once the session is
  // known. The id in the URL wins over whatever is loaded, so browser back to
  // a different dataset switches to it.
  const routeDatasetId = route.datasetId;
  const loadedDatasetId = dataset?.id ?? null;
  useEffect(() => {
    if (session === null || routeDatasetId === null || routeDatasetId === loadedDatasetId) return;
    void openDataset(routeDatasetId, undefined, route.page);
    // route.page is read, not a trigger: the page alone never re-opens a dataset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, routeDatasetId, loadedDatasetId, openDataset]);

  async function narrate(): Promise<void> {
    if (report === null) return;
    setNarrating(true);
    setFailure(null);
    try {
      const { narration } = await api.narrate(report.id, {});
      setReport({ ...report, narrative: narration });
    } catch (error) {
      setFailure(asFailure(error));
    } finally {
      setNarrating(false);
    }
  }

  const hasData = dataset !== null;

  // A share link is a public page: no session, no shell, no redirect to login.
  const sharePath = /^\/s\/([a-z0-9]+)$/.exec(window.location.pathname);
  if (sharePath !== null) return <SharedReportView code={sharePath[1]!} />;

  // A portal link likewise; its access key rides in the URL fragment, which
  // the browser never sends to the server. PortalView reads it from there.
  const portalPath = /^\/portal\/([a-z0-9-]+)$/.exec(window.location.pathname);
  if (portalPath !== null) return <PortalView slug={portalPath[1]!} />;

  if (checkingSession) {
    return (
      <div className="auth-shell">
        <p className="muted">
          <span className="spinner" /> Loading…
        </p>
      </div>
    );
  }

  if (session === null) {
    return (
      <LandingPage
        onSignedIn={() => {
          void api.me().then((restored) => {
            setSession(restored);
            setActiveWorkspace(restored.workspaces[0]?.id ?? null);
            refreshDatasets();
          });
        }}
      />
    );
  }

  const activeWorkspace: Workspace | undefined =
    session.workspaces.find((w) => w.id === restoreActiveWorkspace()) ?? session.workspaces[0];
  const canAdminister = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'admin';

  const paletteItems: PaletteItem[] = [
    ...NAV.flatMap((group) =>
      group.items
        .filter((item) => item.id !== 'admin' || canAdminister)
        .map((item) => ({
          id: `page-${item.id}`,
          label: item.label,
          group: group.group,
          icon: item.icon,
          keywords: item.keywords ?? '',
          run: () => navigate(item.id, dataset?.id ?? null),
        })),
    ),
    ...datasets.slice(0, 20).map((entry) => ({
      id: `dataset-${entry.id}`,
      label: entry.name,
      group: 'Dataset',
      icon: 'file',
      keywords: 'dataset open',
      run: () => void openDataset(entry.id),
    })),
    {
      id: 'action-theme',
      label: mode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
      group: 'Action',
      icon: mode === 'dark' ? 'sun' : 'moon',
      keywords: 'theme dark light mode',
      run: () => setMode(mode === 'dark' ? 'light' : 'dark'),
    },
  ];

  return (
    <div className={navOpen ? 'shell nav-open' : 'shell'}>
      <div className="nav-scrim" onClick={() => setNavOpen(false)} aria-hidden />
      <nav className="sidebar" aria-label="Main">
        <div className="logo">
          <span className="mark" aria-hidden />
          DataVantage
          <button className="nav-close" aria-label="Close menu" onClick={() => setNavOpen(false)}>
            <Icon name="close" size={18} />
          </button>
        </div>

        <button className="nav-search" onClick={() => setPaletteOpen(true)}>
          <Icon name="search" size={15} />
          <span>Search</span>
          <kbd>Ctrl K</kbd>
        </button>

        {NAV.map((group) => {
          const isCollapsed = collapsed.has(group.group) && !group.items.some((item) => item.id === page);
          return (
            <div className="nav-group" key={group.group}>
              <button
                className="nav-group-toggle"
                aria-expanded={!isCollapsed}
                onClick={() => toggleGroup(group.group)}
              >
                <span>{group.group}</span>
                <Icon name="chevronDown" size={13} className={isCollapsed ? 'chev collapsed' : 'chev'} />
              </button>
              {!isCollapsed &&
                group.items.map((item) =>
                  // The admin area is owner/admin only; other roles never see it.
                  item.id === 'admin' && !canAdminister ? null : (
                    <button
                      key={item.id}
                      className="nav-item"
                      aria-current={page === item.id ? 'page' : undefined}
                      data-pending={item.needsData && !hasData ? 'true' : undefined}
                      onClick={() => navigate(item.id, dataset?.id ?? null)}
                      title={item.needsData && !hasData ? `${item.label} — load a dataset first` : item.label}
                    >
                      <Icon name={item.icon} size={16} className="glyph" />
                      <span className="nav-label">{item.label}</span>
                    </button>
                  ),
                )}
            </div>
          );
        })}

        <div className="spacer" />

        {datasets.length > 0 && (
          <div className="nav-group">
            <span className="label">Recent datasets</span>
            {datasets.slice(0, 5).map((entry) => (
              <button
                key={entry.id}
                className="nav-item"
                aria-current={dataset?.id === entry.id ? 'page' : undefined}
                onClick={() => void openDataset(entry.id)}
                title={`${entry.rowCount.toLocaleString('en-US')} rows · ${entry.columnCount} columns`}
              >
                <Icon name="file" size={15} className="glyph" />
                <span className="nav-label">{entry.name}</span>
              </button>
            ))}
          </div>
        )}

        {/* Which store and narrator are live. A deployment silently running
            in memory is otherwise indistinguishable from a healthy one. */}
        <div className="nav-group nav-account">
          {session.workspaces.length > 1 && (
            <select
              className="workspace-select"
              value={activeWorkspace?.id ?? ''}
              aria-label="Workspace"
              onChange={(event) => {
                // Switching tenant invalidates everything on screen, so the
                // loaded dataset and report are cleared rather than left to
                // 404 on their next request.
                setActiveWorkspace(event.target.value);
                setDataset(null);
                setReport(null);
                setDashboardId(null);
                navigate('traffic', null);
                refreshDatasets();
              }}
            >
              {session.workspaces.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>
                  {workspace.name}
                </option>
              ))}
            </select>
          )}

          <div className="account-row">
            <span className="avatar" aria-hidden>
              {(session.user.name || session.user.email).slice(0, 1).toUpperCase()}
            </span>
            <span className="account-text">
              <strong>{session.user.name}</strong>
              <span>{activeWorkspace?.name ?? session.user.email}</span>
            </span>
            <button
              className="icon-button sidebar-icon"
              title="Sign out"
              aria-label="Sign out"
              onClick={async () => {
                await api.logout().catch(() => undefined);
                setActiveWorkspace(null);
                setSession(null);
                setDataset(null);
                setReport(null);
                navigate('traffic', null);
              }}
            >
              <Icon name="logout" size={16} />
            </button>
          </div>
        </div>

        {health !== null && (
          <div className="footnote">
            <span>
              v{health.version} · {health.storage === 'postgres' ? 'postgres' : 'memory'}
            </span>
          </div>
        )}
      </nav>

      <div className="content">
        <header className="topbar">
          <button className="icon-button nav-open-btn" aria-label="Open menu" onClick={() => setNavOpen(true)}>
            <Icon name="menu" size={18} />
          </button>
          <div className="heading">
            <h1>{PAGE_LABEL.get(page) ?? 'DataVantage'}</h1>
            {dataset !== null && NEEDS_DATA.has(page) && (
              <div className="crumb">
                {dataset.name} · {dataset.rowCount.toLocaleString('en-US')} rows · {dataset.columns.length} columns
              </div>
            )}
          </div>

          {report !== null && NEEDS_DATA.has(page) && (
            <div className="row tight export-row">
              {(['csv', 'xlsx', 'pdf', 'looker'] as const).map((format) => (
                <a key={format} href={api.exportUrl(report.id, format)} download>
                  <button className="ghost tiny">{format.toUpperCase()}</button>
                </a>
              ))}
            </div>
          )}

          <button className="topbar-search" onClick={() => setPaletteOpen(true)} aria-label="Search">
            <Icon name="search" size={15} />
            <span>Jump to…</span>
            <kbd>Ctrl K</kbd>
          </button>

          <button
            className="ghost icon-button"
            onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
            aria-label="Toggle theme"
            title={`Theme: ${mode}`}
          >
            <Icon name={mode === 'dark' ? 'sun' : 'moon'} size={16} />
          </button>
        </header>

        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={paletteItems} />

        {failure !== null && (
          <div className="page" style={{ paddingBottom: 0 }}>
            <Notice tone="error" message={failure.message} remedy={failure.remedy} />
          </div>
        )}

        {health !== null && health.storage === 'memory' && health.env === 'production' && (
          <div className="page" style={{ paddingBottom: 0 }}>
            <Notice
              tone="warning"
              message="This deployment has no database connected — accounts and data reset on every restart."
              remedy="Add a Postgres database on Railway and set DATABASE_URL so sign-ups, sessions and uploads persist."
            />
          </div>
        )}

        {page === 'traffic' && (
          <Traffic colors={colors} onNavigate={(next) => navigate(next, null)} />
        )}

        {page === 'realtime' && <Realtime colors={colors} onNavigate={(next) => navigate(next, null)} />}

        {page === 'marketing' && <Marketing colors={colors} onNavigate={(next) => navigate(next, null)} />}

        {/* These pages read the revenue store or the workspace, not the loaded
            dataset, so they render whether or not one is open. */}
        {page === 'revenue' && <RevenueAnalytics />}
        {page === 'attribution' && <AttributionComparison />}
        {page === 'journeys' && <CustomerJourneys />}
        {page === 'campaign-spend' && <CampaignSpendTracker />}
        {page === 'websites' && <WebsiteSetup />}
        {page === 'compare' && <MultiSource colors={colors} />}
        {page === 'portals' && <ClientPortal />}
        {page === 'templates' && <TemplateMarketplace dataset={dataset} />}

        {page === 'sources' && (
          <Sources
            onLoaded={load}
            onOpenDataset={(datasetId, reportId) => void openDataset(datasetId, reportId)}
          />
        )}

        {/*
         * Mounted only while selected, and remounted on every visit.
         * The page owns a whole dashboard plus its filter state; keeping
         * that alive behind six other tabs holds a full analysis in memory
         * for a screen nobody is looking at.
         */}
        {page === 'ai' && <AiAnalytics themeMode={mode} />}

        {page === 'help' && (
          <HowItWorks
            onStart={() => navigate('sources', null)}
            onNavigate={(next) => navigate(next, dataset?.id ?? null)}
          />
        )}

        {page === 'alerts' && <Alerts />}

        {page === 'settings' && (
          <Settings reportId={report?.id ?? null} datasetId={dataset?.id ?? null} />
        )}

        {page === 'admin' && (
          <AdminPanel role={activeWorkspace?.role ?? 'viewer'} userId={session.user.id} />
        )}

        {/* Funnels and retention query the revenue store, not the loaded
            dataset — they render outside the dataset gate. */}
        {page === 'funnels' && <Funnels />}

        {page === 'retention' && <Retention />}

        {page === 'reports' && (
          <div className="page">
            <header className="intro">
              <h2>Reports</h2>
              <p>Every import saves one. Open it to work with the data again, or export it as it stands.</p>
            </header>
            <ReportsTab onOpen={(next) => void openDataset(next.datasetId, next.id)} />
          </div>
        )}

        {dataset === null && NEEDS_DATA.has(page) && (
          <div className="page">
            <div className="card empty">
              <h3>{EMPTY_COPY[page]?.title ?? 'Nothing loaded yet'}</h3>
              <p>{EMPTY_COPY[page]?.body ?? 'Add a source to get started.'}</p>
              <button className="primary" onClick={() => navigate('sources', null)}>
                Add a source
              </button>
            </div>
          </div>
        )}

        {dataset !== null && (
          <>
            {page === 'overview' && report !== null && (
              <div className="page">
                {warnings.map((warning, index) => (
                  <Notice key={index} message={warning} />
                ))}

                <Scorecards cards={report.scorecards} />

                <NarrativePanel narrative={report.narrative} busy={narrating} onGenerate={narrate} />

                {chartFailure !== null && (
                  <Notice tone="error" message={chartFailure.message} remedy={chartFailure.remedy} />
                )}

                <section className="grid">
                  {chartsBusy && (
                    <div className="card">
                      <p className="muted">
                        <span className="spinner" /> Recomputing the charts from the stored rows…
                      </p>
                    </div>
                  )}
                  {report.charts
                    // Only the kinds this grid can actually draw from bucket
                    // data. A scatter or a histogram rendered from groups is a
                    // bar chart wearing the wrong title; the AI analytics page
                    // draws both properly from the points themselves.
                    .filter((suggestion) => suggestion.chartType !== 'scatter' && suggestion.chartType !== 'histogram')
                    .slice(0, 4)
                    .map((suggestion) => {
                    const buckets = chartData[suggestion.id];
                    if (buckets === undefined || buckets.length === 0) return null;
                    const label =
                      suggestion.y === undefined ? 'rows' : `${suggestion.aggregation}(${suggestion.y})`;

                    return (
                      <div className="card" key={suggestion.id}>
                        <header>
                          <h2>{suggestion.title}</h2>
                        </header>
                        {suggestion.chartType === 'line' ? (
                          <TrendChart buckets={buckets} measureLabel={label} colors={colors} />
                        ) : (
                          <CategoryChart buckets={buckets} measureLabel={label} colors={colors} />
                        )}
                        {/* Why this chart was suggested, not just what it shows. */}
                        <p className="muted" style={{ marginTop: 8, marginBottom: 0 }}>
                          {suggestion.rationale}
                        </p>
                      </div>
                    );
                    })}
                </section>

                <section className="grid">
                  <Insights insights={report.insights} />
                  <QualityPanel quality={report.quality} />
                </section>
              </div>
            )}

            {page === 'overview' && report === null && (
              <div className="page">
                <div className="card empty">
                  <h3>No saved report for this dataset</h3>
                  <p>The data is loaded — explore it, pivot it, or build a dashboard.</p>
                  <button className="primary" onClick={() => navigate('explore', dataset.id)}>
                    Explore
                  </button>
                </div>
              </div>
            )}

            {page === 'explore' && (
              <div className="page">
                <ExploreTab dataset={dataset} colors={colors} />
              </div>
            )}

            {page === 'data' && (
              <div className="page">
                <DataTab dataset={dataset} />
              </div>
            )}

            {page === 'pivot' && (
              <div className="page">
                <PivotTab dataset={dataset} />
              </div>
            )}

            {page === 'forecast' && <Forecasting dataset={dataset} colors={colors} />}

            {page === 'chat' && (
              <AiChat
                reportId={report?.id ?? null}
                context={{
                  name: dataset.name,
                  rowCount: dataset.rowCount,
                  columnCount: dataset.columns.length,
                }}
              />
            )}

            {page === 'looker' && report !== null && <LookerStudio report={report} />}

            {page === 'looker' && report === null && (
              <div className="page">
                <div className="card empty">
                  <h3>No report for this dataset</h3>
                  <p>A Looker Studio template is built from a saved report. Re-upload to create one.</p>
                </div>
              </div>
            )}

            {page === 'powerbi' && report !== null && <PowerBI report={report} />}

            {page === 'powerbi' && report === null && (
              <div className="page">
                <div className="card empty">
                  <h3>No report for this dataset</h3>
                  <p>Power BI export needs a saved report. Re-upload to create one.</p>
                </div>
              </div>
            )}

            {page === 'client-reports' && report !== null && <ClientReports report={report} />}

            {page === 'client-reports' && report === null && (
              <div className="page">
                <div className="card empty">
                  <h3>No report for this dataset</h3>
                  <p>Client reports are built from a saved analysis. Re-upload to create one.</p>
                </div>
              </div>
            )}

            {page === 'dashboard' && (
              <DashboardBuilder
                dataset={dataset}
                colors={colors}
                dashboardId={dashboardId}
                onSaved={(saved: Dashboard) => setDashboardId(saved.id)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function asFailure(error: unknown): ApiFailure {
  return error instanceof ApiFailure ? error : new ApiFailure('The request failed.', 'Try again.', 0);
}

// Toast and confirm providers wrap everything — share links, portals and the
// unauthenticated landing page included — so any screen can surface feedback.
export function App() {
  return (
    <ToastProvider>
      <ConfirmProvider>
        <AppInner />
      </ConfirmProvider>
    </ToastProvider>
  );
}
